import React, { useMemo } from 'react';
import _ from 'lodash';
import { FaUsers } from 'react-icons/fa';
import { Event } from '../../../../types/event';
import { formatDate, formatDateAndTime } from '../../../../utils/stringUtils';
import { formatShiftCommunitySummary } from './scheduleCommunityUtils';

interface ScheduleShiftsListProps {
  shifts: Event[];
  onShiftClick: (shift: Event) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

type ShiftAssignee = {
  id: number;
  firstName: string;
  lastName: string;
  picture?: string;
};

function getAssigneeInitials(assignee: ShiftAssignee): string {
  const first = assignee.firstName?.charAt(0) ?? '';
  const last = assignee.lastName?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

function getShiftAssignees(shift: Event): ShiftAssignee[] {
  const participants: ShiftAssignee[] = shift.participants.map((participant) => ({
    id: participant.id,
    firstName: participant.firstName,
    lastName: participant.lastName,
    picture: participant.picture,
  }));

  const externalInvites: ShiftAssignee[] = shift.externalInvites.map((invite) => ({
    id: invite.externalInviteId,
    firstName: invite.firstName,
    lastName: invite.lastName,
    picture: invite.picture,
  }));

  return [...participants, ...externalInvites];
}

export default function ScheduleShiftsList({
  shifts,
  onShiftClick,
  t,
}: ScheduleShiftsListProps): JSX.Element {
  const shiftsByDate = useMemo(
    () => _.groupBy(shifts, (shift) => formatDate(shift.startTime)),
    [shifts],
  );

  return (
    <div className="flex flex-col gap-2">
      {_.map(shiftsByDate, (shiftsForDate, date) => (
        <React.Fragment key={date}>
          <div className="border-b border-primary-300 py-1 text-sm font-bold text-primary">
            {date}
          </div>
          {shiftsForDate.map((shift, index) => {
            const assignees = getShiftAssignees(shift);
            const visibleAssignees = assignees.slice(0, 4);
            const remainingAssigneesCount = assignees.length - visibleAssignees.length;
            const communitySummary = formatShiftCommunitySummary(shift, t);

            return (
              <button
                key={`${shift.id}-${index}`}
                type="button"
                onClick={() => onShiftClick(shift)}
                className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex flex-col gap-1 text-left">
                  <span className="text-sm font-semibold text-primary-900">{shift.title}</span>
                  {shift.startTime && (
                    <span className="text-xs text-primary-500">
                      {formatDateAndTime(new Date(shift.startTime))}
                      {shift.endTime ? ` - ${formatDateAndTime(new Date(shift.endTime))}` : ''}
                    </span>
                  )}
                  {communitySummary && (
                    <span className="text-xs text-primary-600">{communitySummary}</span>
                  )}
                  {assignees.length > 0 && (
                    <div className="mt-1 flex items-center">
                      <div className="flex -space-x-2">
                        {visibleAssignees.map((assignee) => (
                          <div
                            key={`assignee-${assignee.id}-${assignee.firstName}-${assignee.lastName}`}
                            className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border border-secondary-50 bg-primary-100 text-[10px] font-semibold text-primary-900"
                            title={`${assignee.firstName} ${assignee.lastName}`}>
                            {assignee.picture ? (
                              <img
                                src={assignee.picture}
                                alt={`${assignee.firstName} ${assignee.lastName}`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              getAssigneeInitials(assignee)
                            )}
                          </div>
                        ))}
                        {remainingAssigneesCount > 0 && (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-secondary-50 bg-primary-200 text-[10px] font-semibold text-primary-900">
                            +{remainingAssigneesCount}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-md text-primary-400">
                    <FaUsers className="h-3 w-3" />
                    <span>
                      {assignees.length}
                      {shift.maximumAttendees ? ` / ${shift.maximumAttendees}` : ''}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
